import React, { useState, useEffect, useRef, useCallback } from "react";
import "./style/visitorPicture.scss";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import Webcam from "react-webcam";
import { showError } from "../../services/toastservice/toastService";
import { BackButton } from "../../components/ui/Button/backButton";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useCamera } from "../../hooks/useCamera";
import { useEnhancedIdChecks } from "../../hooks/useEnhancedIdChecks";
import { useStrings } from "../../hooks/useStrings";
import { shouldShowIdCapture } from "../../utils/idCapture";
import { shouldShowVideo } from "../../utils/videoStep";

// EN-3744: silent recovery for transient camera-busy errors.
// See handleInitError below.
const MAX_AUTO_RETRIES = 2;
const RETRY_DELAYS_MS = [600, 1500];

const VisitorPicture: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  // const dispatch = useDispatch<AppDispatch>();
  const webcamRef = useRef<Webcam>(null);

  // State from Angular component
  const [, setMultipleWebcamsAvailable] = useState(false);
  const [facingMode] = useState<"user" | "environment">("user");

  const { themeColors } = useAppConfig();
  const { permissionState, requestPermission } = useCamera();
  const { startEnhancedIdChecks } = useEnhancedIdChecks();

  const {
    visitorinfo: reduxVisitorInfo,
    videoUrl,
    flowInfoFlags,
    idValidationSkip,
    videoSkip,
    orgTemplateIds,
    updateVisitorInfo,
  } = useVisitorData();

  const [visitorIDPicture, setVisitorIDPicture] = useState<string>("");
  const [isCaptured, setIsCaptured] = useState(false);

  // Camera-busy recovery (EN-3744). Android Chrome can reject the first
  // getUserMedia() after a route change with NotReadableError while the
  // camera HAL is still warming up. We silently remount <Webcam> up to
  // MAX_AUTO_RETRIES times before falling back to a manual retry button.
  const [webcamKey, setWebcamKey] = useState(0);
  const [cameraStatus, setCameraStatus] = useState<"ok" | "retrying" | "failed">("ok");
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only treat cameraStatus as meaningful while permission is granted —
  // any other permission state (denied/prompt/unsupported) takes over the UI
  // through its own dedicated branches below. Deriving this avoids an effect
  // that would otherwise need to setState on permissionState change.
  const effectiveCameraStatus =
    permissionState === "granted" ? cameraStatus : "ok";

  useEffect(() => {
    // Equivalent to ngOnInit
    // Check for multiple webcams
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setMultipleWebcamsAvailable(videoInputs && videoInputs.length > 1);
      })
      .catch(console.error);
  }, []);

  // Cancel any pending auto-retry timer on unmount so we don't bump the
  // webcam key after the user has navigated away.
  useEffect(() => () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const handleInitError = useCallback((error: string | DOMException) => {
    if (typeof error === "string") {
      showError(error);
      return;
    }
    if (error?.name === "NotAllowedError") {
      requestPermission();
      return;
    }

    const transient =
      error?.name === "NotReadableError" || error?.name === "TrackStartError";
    if (!transient) {
      console.error("Camera access error:", error);
      showError(S.VISITOR_PICTURE.TOAST_CAMERA_ERROR);
      return;
    }

    if (retryCountRef.current < MAX_AUTO_RETRIES) {
      const delay = RETRY_DELAYS_MS[retryCountRef.current];
      retryCountRef.current += 1;
      setCameraStatus("retrying");
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        setWebcamKey((k) => k + 1);
      }, delay);
    } else {
      setCameraStatus("failed");
    }
  }, [requestPermission, S.VISITOR_PICTURE.TOAST_CAMERA_ERROR]);

  const handleCameraReady = useCallback(() => {
    retryCountRef.current = 0;
    setCameraStatus("ok");
  }, []);

  const handleManualRetry = useCallback(() => {
    // After a manual retry, skip silent auto-retries — the user explicitly
    // chose to retry, so any further failure should surface immediately.
    retryCountRef.current = MAX_AUTO_RETRIES;
    setCameraStatus("ok");
    setWebcamKey((k) => k + 1);
  }, []);

  const handleImage = useCallback(
    (imageDataUrl: string) => {
      const visitorInfoData = reduxVisitorInfo;

      // Convert data URL to base64 (remove data:image/jpeg;base64, prefix)
      const base64Image = imageDataUrl.split(",")[1];

      const updatedVisitorInfo = {
        ...visitorInfoData,
        visitPhotoImage: base64Image,
      };

      updateVisitorInfo(updatedVisitorInfo);
      setVisitorIDPicture(imageDataUrl);
      setIsCaptured(true);
    },
    [reduxVisitorInfo, updateVisitorInfo],
  );

  const triggerSnapshot = useCallback(() => {
    if (!webcamRef.current) return;

    const video = webcamRef.current.video; // native video element
    if (!video) return;
    if (video.readyState !== 4) return; // ensure video is ready

    // Create a canvas with the SAME resolution as the webcam video
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Highest quality JPEG or PNG
    const imageSrc = canvas.toDataURL("image/jpeg", 1.0);

    handleImage(imageSrc);
  }, [handleImage]);

  const onRetake = useCallback(() => {
    setIsCaptured(false);
    setVisitorIDPicture("");
    const updatedVisitorInfo = {
      ...reduxVisitorInfo,
      visitPhotoImage: null,
    };
    updateVisitorInfo(updatedVisitorInfo);
  }, [reduxVisitorInfo, updateVisitorInfo]);

  const onConfirm = useCallback(() => {
    // EN-5235: name and photo are both in hand now; start before the visit is created.
    startEnhancedIdChecks();
    if (shouldShowVideo(videoUrl, videoSkip)) {
      navigate("/visitor-video");
    } else {
      navigate("/confirmation", { replace: true });
    }
  }, [videoUrl, videoSkip, navigate, startEnhancedIdChecks]);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode,
  };

  const onBack = useCallback(() => {
    // EN-4518: when the ID step was skipped it is not behind us, so back must
    // land on whatever step actually preceded this one.
    if (shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)) {
      navigate("/frontbackpicture");
    } else if (orgTemplateIds && orgTemplateIds.length > 0) {
      navigate("/signdocument");
    } else {
      navigate("/visitorform");
    }
  }, [flowInfoFlags, idValidationSkip, orgTemplateIds, navigate]);

  return (
    <>
      <div className="main-screen" data-testid="visitor-picture-main-screen">
        <div
          className="screen-outer screen-visitorpicture"
          id="visitor-picture-page"
          data-testid="visitor-picture-screen-outer"
        >
          <div
            className="screen-header-fixed"
            data-testid="visitor-picture-header-fixed"
          >
            <div
              className="header-fixed-left"
              data-testid="visitor-picture-header-left"
            >
              <BackButton onBack={onBack} />
            </div>
            <div
              className="header-fixed-right"
              data-testid="visitor-picture-header-right"
            ></div>
          </div>

          <div
            className="screen-inner"
            // style={{ height: "90vh" }}
            data-testid="visitor-picture-screen-inner"
          >
            <div
              className="screen-subinner"
              data-testid="visitor-picture-screen-subinner"
            >
              <div
                className="screen-body"
                data-testid="visitor-picture-screen-body"
              >
                <h5
                  className="screen-title"
                  data-testid="visitor-picture-title"
                >
                  {S.VISITOR_PICTURE.TITLE}
                </h5>
                <p
                  className="screen-subtitle"
                  data-testid="visitor-picture-subtitle"
                >
                  {S.VISITOR_PICTURE.SUBTITLE}
                </p>

                <div
                  className="visitor-picture"
                  data-testid="visitor-picture-container"
                >
                  {!isCaptured &&
                    permissionState === "granted" &&
                    effectiveCameraStatus !== "failed" && (
                      <div data-testid="visitor-picture-webcam-wrapper">
                        <Webcam
                          key={webcamKey}
                          ref={webcamRef}
                          audio={false}
                          screenshotFormat="image/jpeg"
                          videoConstraints={videoConstraints}
                          onUserMediaError={handleInitError}
                          onUserMedia={handleCameraReady}
                          screenshotQuality={1}
                          mirrored={true}
                          imageSmoothing={false}
                        />
                      </div>
                    )}

                  {!isCaptured &&
                    permissionState === "granted" &&
                    effectiveCameraStatus === "failed" && (
                      <div
                        className="camera-permission-placeholder"
                        data-testid="visitor-picture-camera-failed-placeholder"
                      >
                        <i className="bi bi-camera-video-off" />
                      </div>
                    )}

                  {!isCaptured && permissionState !== "granted" && (
                    <div
                      className="camera-permission-placeholder"
                      data-testid="camera-placeholder"
                    >
                      <i className="bi bi-camera-video-off" />
                    </div>
                  )}

                  {isCaptured && visitorIDPicture && (
                    <img
                      src={visitorIDPicture}
                      alt="Visitor Image"
                      data-testid="visitor-picture-preview-image"
                    />
                  )}
                </div>
                <p
                  className="visitor-picture-footer-text mt-3"
                  data-testid="visitor-picture-instructions"
                >
                  {isCaptured
                    ? S.VISITOR_PICTURE.INSTRUCTIONS
                    : permissionState === "granted"
                      ? effectiveCameraStatus === "failed"
                        ? S.VISITOR_PICTURE.CAMERA_NOT_AVAILABLE
                        : S.VISITOR_PICTURE.INSTRUCTIONS
                      : permissionState === "denied"
                        ? S.VISITOR_PICTURE.PERMISSION_DENIED
                        : permissionState === "unsupported"
                          ? S.VISITOR_PICTURE.PERMISSION_UNSUPPORTED
                          : S.VISITOR_PICTURE.PERMISSION_PROMPT}
                </p>
                <div
                  className="mt-2"
                  data-testid="visitor-picture-action-container"
                >
                  {isCaptured && (
                    <>
                      <Button
                        type="button"
                        className="btn btn-retake me-2 btn-shadow"
                        onClick={onRetake}
                        data-testid="visitor-picture-retake-button"
                      >
                        <i className="bi bi-arrow-repeat me-2"></i>
                        {S.VISITOR_PICTURE.BTN_RETAKE}
                      </Button>
                    </>
                  )}

                  {!isCaptured && permissionState !== "granted" && (
                    <button
                      type="button"
                      className="btn btn-enable-camera btn-shadow"
                      onClick={requestPermission}
                      disabled={
                        permissionState === "unsupported" ||
                        permissionState === "checking"
                      }
                      style={{
                        backgroundColor: themeColors.btnColor,
                        borderColor: themeColors.btnColor,
                        color: themeColors.btnTextColor,
                      }}
                      data-testid="visitor-picture-enable-camera-button"
                    >
                      <i className="bi bi-camera-video me-2"></i>
                      {S.VISITOR_PICTURE.BTN_ENABLE_CAMERA}
                    </button>
                  )}

                  {!isCaptured &&
                    permissionState === "granted" &&
                    effectiveCameraStatus !== "failed" && (
                      <button
                        type="button"
                        className="btn capture-button btn-shadow"
                        onClick={triggerSnapshot}
                        disabled={effectiveCameraStatus === "retrying"}
                        data-testid="visitor-picture-capture-button"
                      >
                        <i className="bi bi-circle me-2"></i>
                        {S.VISITOR_PICTURE.BTN_CAPTURE}
                      </button>
                    )}

                  {!isCaptured &&
                    permissionState === "granted" &&
                    effectiveCameraStatus === "failed" && (
                      <button
                        type="button"
                        className="btn btn-enable-camera btn-shadow"
                        onClick={handleManualRetry}
                        style={{
                          backgroundColor: themeColors.btnColor,
                          borderColor: themeColors.btnColor,
                          color: themeColors.btnTextColor,
                        }}
                        data-testid="visitor-picture-retry-camera-button"
                      >
                        <i className="bi bi-arrow-repeat me-2"></i>
                        {S.VISITOR_PICTURE.BTN_RETRY_CAMERA}
                      </button>
                    )}
                </div>
                <Button
                  type="button"
                  variant="primary"
                  className="btn-shadow btn-min-width mt-auto"
                  disabled={!isCaptured}
                  onClick={onConfirm}
                  style={{
                    backgroundColor: themeColors.btnColor,
                    borderColor: themeColors.btnColor,
                    color: themeColors.btnTextColor,
                  }}
                  data-testid="visitor-picture-next-button"
                >
                  {S.COMMON.CONTINUE} &nbsp;&nbsp;
                  <i className="bi bi-arrow-right-short"></i>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VisitorPicture;
